import { useCallback, useState } from 'react';
import { deviceType } from 'detect-it';
import { useDocumentVisibility } from '@doxyme/ui/hooks';
import { useLocalMedia, useLocalMediaActions } from '../useLocalMedia';

/**
 *  This hook is used to pause video when the document is not visible on mobile devices.
 *  Remote participants will see a placeholder instead of a black screen.
 *
 *  @warning This hook should be rendered once. Multiple renders will cause multiple listeners and unexpected behavior.
 */
export const usePauseVideoOnMobileBackground = (): void => {
  const [isPaused, setIsPaused] = useState(false);

  const { hasVideo } = useLocalMedia();
  const { toggleVideo } = useLocalMediaActions();

  const onVisibilityChange = useCallback(
    (isVisible: boolean) => {
      if (deviceType !== 'touchOnly') {
        return;
      }

      if (!hasVideo && !isPaused) {
        return;
      }

      if (isVisible && isPaused) {
        setIsPaused(false);

        void toggleVideo(true);
      } else if (!isVisible && !isPaused) {
        setIsPaused(true);

        void toggleVideo(false);
      }
    },
    [isPaused, hasVideo, toggleVideo],
  );

  useDocumentVisibility(onVisibilityChange, { callOnInit: true });
};
